// Pure formatting for « Arrivées · Départs » — the team's forward-looking list.
// The same conventions as the desk sheets (apps/pms/src/departureSheet.ts),
// restated here because an app does not import another app's internals.

import type { Instant } from '@lemontaigu/pms-core';

/** '0214' → '214' — the number on the door. */
export function displayRoom(room: string): string {
  return /^\d+$/.test(room) ? String(Number(room)) : room;
}

/** From this many rooms a booking reads as a count, not a list (the desk
 *  sheets' ROOMS_BY_FLOOR_FROM) — a group of 18 would fill a phone screen. */
export const ROOMS_AS_COUNT_FROM = 6;

/** « 214 », « 101 · 102 », « 18 chambres ». A booking with no room yet reads « — ». */
export function roomsLabel(rooms: string[]): string {
  const doors = [...new Set(rooms.map(displayRoom))];
  if (doors.length === 0) return '—';
  return doors.length >= ROOMS_AS_COUNT_FROM ? `${doors.length} chambres` : doors.join(' · ');
}

export function nightsLabel(n: number): string {
  return `${n} nuit${n > 1 ? 's' : ''}`;
}

/** 'HHMM' → '15:30'; null when Vega holds no time. */
export function hhmm(t: string | null): string | null {
  return t && /^\d{4}$/.test(t) ? `${t.slice(0, 2)}:${t.slice(2)}` : null;
}

/** Euros as the desk writes them, no currency sign: 1135.38 → « 1 135,38 ». */
export function fmtEuros(cents: number): string {
  return (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** What an arrival row says about the money: « — » when the plan prices no
 *  stay, « soldé » when nothing is left, otherwise the amount. A negative
 *  remainder is not a refund to make — it is a fact to check in Vega. */
export function arrivalDueLabel(dueCents: number | null): { text: string; owed: boolean } {
  if (dueCents === null) return { text: '—', owed: false };
  if (dueCents > 0) return { text: fmtEuros(dueCents), owed: true };
  return { text: 'soldé', owed: false };
}

/** The feed checks Vega every minute; a read older than this means it is not
 *  running, and the list says so rather than looking current. */
export const VEGA_READ_STALE_MINUTES = 5;

/** « Vega relu à 14:03 », with ⚠ and its age once stale. Null when unknown. */
export function vegaReadLabel(readAt: Instant | null, now: Date): { text: string; stale: boolean } | null {
  if (!readAt) return null;
  const at = new Date(readAt);
  if (Number.isNaN(at.getTime())) return null;
  const time = at.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' });
  const minutes = Math.floor((now.getTime() - at.getTime()) / 60_000);
  if (minutes <= VEGA_READ_STALE_MINUTES) return { text: `Vega relu à ${time}`, stale: false };
  const age = minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`;
  return { text: `⚠ Vega relu à ${time}, il y a ${age}`, stale: true };
}
